import { supabase } from '../lib/supabase';
import arresteeService from './arresteeService';
import authService from './authService';

class UploadService {
  // Create upload batch with enhanced session validation and automatic refresh
  async createBatch(filename, fileSize, fileHash) {
    try {
      // Use enhanced session validation with automatic refresh
      const sessionResult = await authService?.validateAndRefreshSession();
      
      if (!sessionResult?.success) {
        throw new Error(`Authentication failed: ${sessionResult?.error || 'Invalid session'}. Please sign in again.`);
      }

      const { session, user, refreshed } = sessionResult;

      // Log session refresh for debugging
      if (refreshed) {
        console.log('Session automatically refreshed for upload batch creation');
      }

      // Verify user has a profile in the database (required for RLS policies)
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, is_active, role')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) {
        if (profileError?.code === 'PGRST116') {
          throw new Error('User profile not found. Please contact your administrator to set up your account.');
        }
        throw new Error(`Profile verification failed: ${profileError?.message}. Please try again or contact support.`);
      }

      if (!profile?.is_active) {
        throw new Error('Your account is inactive. Please contact your administrator to reactivate your account.');
      }

      // Check if user has permission to upload files (all roles can upload)
      if (!['admin', 'agent', 'operator']?.includes(profile?.role)) {
        throw new Error('You do not have permission to upload files. Please contact your administrator.');
      }

      const { data, error } = await supabase?.from('upload_batches')?.insert({
          filename,
          file_size: fileSize,
          file_hash: fileHash,
          status: 'processing',
          uploaded_by: user?.id
        })?.select()?.single();

      if (error) {
        if (error?.code === '42501') {
          throw new Error('Database permission denied. Your session may have expired. Please sign out and sign in again.');
        } else if (error?.code === '23503') {
          throw new Error('User profile validation failed. Please contact your administrator.');
        }
        throw new Error(`Failed to create upload batch: ${error?.message}`);
      }

      return { success: true, data };
    } catch (error) {
      // Enhanced error logging for debugging
      console.error('Upload batch creation failed:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        timestamp: new Date()?.toISOString()
      });
      
      return { success: false, error: error?.message, data: null };
    }
  }

  // Update batch progress
  async updateBatchProgress(batchId, processed, duplicates, status = 'processing', diagnosticData = {}) {
    try {
      const updates = {
        processed_records: processed,
        duplicate_records: duplicates,
        status: status,
        updated_at: new Date()?.toISOString(),
        last_activity_at: new Date()?.toISOString() // Track activity for stuck detection
      };

      // Add diagnostic fields
      if (diagnosticData?.rowsInFile !== undefined) {
        updates.rows_in_file = diagnosticData?.rowsInFile;
      }
      if (diagnosticData?.rowsInserted !== undefined) {
        updates.rows_inserted = diagnosticData?.rowsInserted;
      }
      if (diagnosticData?.rowsSkippedDuplicates !== undefined) {
        updates.rows_skipped_duplicates = diagnosticData?.rowsSkippedDuplicates;
      }
      if (diagnosticData?.rowsErrors !== undefined) {
        updates.rows_errors = diagnosticData?.rowsErrors;
      }
      if (diagnosticData?.importLog && Array.isArray(diagnosticData?.importLog)) {
        // Limit import log to first 1000 errors to keep storage reasonable
        updates.import_log = diagnosticData?.importLog?.slice(0, 1000);
      }
      if (diagnosticData?.processingMode) {
        updates.processing_mode = diagnosticData?.processingMode;
      }

      if (status === 'completed') {
        updates.completed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('upload_batches')?.update(updates)?.eq('id', batchId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // New preflight validation method
  async validateFilePreflight(file, batchId) {
    try {
      const sampleSize = Math.min(1024, file?.size); // First 1KB for analysis
      const sampleContent = await file?.slice(0, sampleSize)?.text();
      
      // Detect encoding
      let detectedEncoding = 'UTF-8';
      try {
        // Try to decode as UTF-8
        new TextDecoder('utf-8', { fatal: true })?.decode(await file?.slice(0, 100)?.arrayBuffer());
      } catch (e) {
        detectedEncoding = 'cp1252'; // Fallback for Windows encoding
      }
      
      // Detect delimiter by analyzing first line
      const firstLine = sampleContent?.split('\n')?.[0] || '';
      const commaCount = (firstLine?.match(/,/g) || [])?.length;
      const semicolonCount = (firstLine?.match(/;/g) || [])?.length;
      
      const detectedDelimiter = (semicolonCount > 2 && semicolonCount > commaCount) ? ';' : ',';
      const columnCount = detectedDelimiter === ';' ? semicolonCount + 1 : commaCount + 1;
      
      // Expected columns for arrestee data
      const expectedColumns = 44;
      
      // Determine validation status
      let preflightStatus = 'passed';
      let preflightErrors = [];
      
      if (columnCount < expectedColumns) {
        preflightStatus = 'failed';
        preflightErrors?.push({
          type: 'header_mismatch',
          message: `Missing columns: found ${columnCount}, expected ${expectedColumns}`,
          severity: 'error'
        });
      } else if (columnCount > expectedColumns) {
        preflightStatus = 'warning';
        preflightErrors?.push({
          type: 'header_mismatch', 
          message: `Extra columns detected: found ${columnCount}, expected ${expectedColumns}`,
          severity: 'warning'
        });
      }
      
      // Check for BOM
      if (sampleContent?.charCodeAt(0) === 0xFEFF) {
        preflightErrors?.push({
          type: 'encoding',
          message: 'BOM detected and will be stripped',
          severity: 'info'
        });
      }
      
      // Update batch with preflight results
      const { error } = await supabase?.from('upload_batches')?.update({
        file_encoding: detectedEncoding,
        file_delimiter: detectedDelimiter,
        detected_headers: [firstLine],
        preflight_status: preflightStatus,
        preflight_errors: preflightErrors
      })?.eq('id', batchId);
      
      if (error) {
        throw error;
      }
      
      return {
        success: true,
        encoding: detectedEncoding,
        delimiter: detectedDelimiter,
        columnCount,
        status: preflightStatus,
        errors: preflightErrors
      };
      
    } catch (error) {
      return {
        success: false,
        error: error?.message,
        encoding: 'UTF-8',
        delimiter: ',',
        columnCount: 0,
        status: 'failed',
        errors: [{ type: 'preflight', message: error?.message, severity: 'error' }]
      };
    }
  }

  // Enhanced processBatchData with comprehensive diagnostics and dead-letter tracking
  async processBatchData(batchId, records, isDryRun = false) {
    let processed = 0;
    let duplicates = 0;
    let inserted = 0;
    let errorCount = 0;
    const errors = [];
    const importLog = [];

    try {
      const rowsInFile = records?.length || 0;
      
      // Update total records count and processing mode
      await supabase?.from('upload_batches')?.update({ 
        total_records: rowsInFile,
        processing_mode: isDryRun ? 'dry_run' : 'normal',
        last_activity_at: new Date()?.toISOString()
      })?.eq('id', batchId);

      // Process in smaller chunks to handle larger files
      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < records?.length; i += chunkSize) {
        chunks?.push(records?.slice(i, i + chunkSize));
      }

      for (let chunkIndex = 0; chunkIndex < chunks?.length; chunkIndex++) {
        const chunk = chunks?.[chunkIndex];
        
        for (const [rowIndex, record] of chunk?.entries()) {
          const currentRow = chunkIndex * chunkSize + rowIndex + 1;
          
          try {
            // Enhanced validation with detailed error tracking
            const validationResult = this.validateRecordFields(record, currentRow);
            
            if (!validationResult?.isValid) {
              errorCount++;
              importLog?.push(...validationResult?.errors);
              
              // Insert validation errors into dead-letter table
              if (!isDryRun) {
                await this.insertDeadLetterRecord(batchId, currentRow, record, validationResult?.errors?.[0]?.message || 'Validation error');
              }
              continue;
            }

            // Enhanced duplicate checking
            const duplicateCheck = await arresteeService?.checkDuplicate(
              record?.first_name,
              record?.last_name, 
              record?.date_of_birth
            );

            if (duplicateCheck?.duplicateId) {
              duplicates++;
              if (!isDryRun) {
                // Still insert for historical tracking but mark as duplicate
                record.status = 'duplicate';
                record.duplicate_of = duplicateCheck?.duplicateId;
              }
            }

            if (!isDryRun) {
              // Create the actual record
              const recordData = this.normalizeRecordData(record, batchId);
              const result = await arresteeService?.createRecord(recordData);
              
              if (result?.success) {
                inserted++;
              } else {
                errorCount++;
                const errorMessage = `Insert failed: ${result?.error}`;
                importLog?.push({
                  line_number: currentRow,
                  message: errorMessage,
                  severity: 'error',
                  column: 'database_insert'
                });
                
                // Insert database errors into dead-letter table
                await this.insertDeadLetterRecord(batchId, currentRow, record, errorMessage);
              }
            } else {
              // Dry run - just count what would be inserted
              inserted++;
            }

            processed++;

          } catch (error) {
            errorCount++;
            const errorMessage = `Row processing error: ${error?.message}`;
            importLog?.push({
              line_number: currentRow,
              message: errorMessage,
              severity: 'error',
              column: 'row_processing',
              raw_value: JSON.stringify(record)?.slice(0, 100) // Limit size
            });
            
            // Insert processing errors into dead-letter table
            if (!isDryRun) {
              await this.insertDeadLetterRecord(batchId, currentRow, record, errorMessage);
            }
          }
        }

        // Update progress with detailed diagnostics after each chunk
        await this.updateBatchProgress(batchId, processed, duplicates, 'processing', {
          rowsInFile,
          rowsInserted: inserted,
          rowsSkippedDuplicates: duplicates,
          rowsErrors: errorCount,
          importLog: importLog?.slice(0, 10), // First 10 errors for UI display
          processingMode: isDryRun ? 'dry_run' : 'normal'
        });
      }

      // Final update with comprehensive diagnostics
      const status = errorCount > 0 ? 'completed_with_errors' : 'completed';
      await this.updateBatchProgress(batchId, processed, duplicates, status, {
        rowsInFile,
        rowsInserted: inserted,
        rowsSkippedDuplicates: duplicates,
        rowsErrors: errorCount,
        importLog: importLog?.slice(0, 1000), // Store first 1000 errors
        processingMode: isDryRun ? 'dry_run' : 'normal'
      });

      // Update batch with final error message if needed
      if (errorCount > 0) {
        const errorSummary = this.generateErrorSummary(importLog);
        await supabase?.from('upload_batches')?.update({ 
          error_message: errorSummary 
        })?.eq('id', batchId);
      }

      return {
        success: true,
        processed,
        duplicates,
        inserted,
        errors: errorCount,
        errorMessages: importLog?.slice(0, 20), // Return first 20 for immediate display
        isDryRun,
        summary: {
          rowsInFile,
          rowsInserted: inserted,
          rowsSkippedDuplicates: duplicates,
          rowsErrors: errorCount
        }
      };

    } catch (error) {
      await this.updateBatchProgress(batchId, processed, duplicates, 'failed', {
        rowsInFile: records?.length || 0,
        rowsInserted: inserted,
        rowsSkippedDuplicates: duplicates,
        rowsErrors: errorCount,
        importLog: importLog,
        processingMode: isDryRun ? 'dry_run' : 'normal'
      });

      await supabase?.from('upload_batches')?.update({ 
        error_message: error?.message 
      })?.eq('id', batchId);

      return {
        success: false,
        error: error?.message,
        processed,
        duplicates,
        inserted,
        errors: errorCount,
        isDryRun
      };
    }
  }

  // New method to insert dead-letter records
  async insertDeadLetterRecord(batchId, rowNum, payload, errorText) {
    try {
      await supabase?.from('ingest_deadletter')?.insert({
        batch_id: batchId,
        row_num: rowNum,
        payload: payload,
        error_text: errorText,
        error_severity: 'error'
      });
    } catch (error) {
      console.warn('Failed to insert dead-letter record:', error?.message);
    }
  }

  // New method for detailed record validation
  validateRecordFields(record, lineNumber) {
    const errors = [];
    let isValid = true;

    // Required fields validation
    if (!record?.first_name?.trim()) {
      errors?.push({
        line_number: lineNumber,
        message: 'ValidationError: missing required field first_name',
        column: 'first_name',
        severity: 'error'
      });
      isValid = false;
    }

    if (!record?.last_name?.trim()) {
      errors?.push({
        line_number: lineNumber,
        message: 'ValidationError: missing required field last_name', 
        column: 'last_name',
        severity: 'error'
      });
      isValid = false;
    }

    if (!record?.date_of_birth) {
      errors?.push({
        line_number: lineNumber,
        message: 'ValidationError: missing required field date_of_birth',
        column: 'date_of_birth', 
        severity: 'error'
      });
      isValid = false;
    } else {
      // Date format validation
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex?.test(record?.date_of_birth)) {
        errors?.push({
          line_number: lineNumber,
          message: `DateParseError: invalid date format "${record?.date_of_birth}"; expected YYYY-MM-DD`,
          column: 'date_of_birth',
          raw_value: record?.date_of_birth,
          severity: 'error'
        });
        isValid = false;
      }
    }

    return { isValid, errors };
  }

  // New method to normalize record data with enhanced field mapping
  normalizeRecordData(record, batchId) {
    return {
      upload_batch_id: batchId,
      first_name: record?.first_name?.trim() || '',
      last_name: record?.last_name?.trim() || '',
      date_of_birth: record?.date_of_birth,
      case_number: record?.case_number?.trim() || record?.case_num?.trim() || '',
      booking_number: record?.booking_number?.trim() || record?.booking_num?.trim() || '',
      arrest_date: record?.arrest_date || record?.arrestdate || null,
      charges: record?.charges?.trim() || record?.charge?.trim() || '',
      address: record?.address?.trim() || '',
      city: record?.city?.trim() || '',
      state: record?.state?.trim() || '',
      zip_code: record?.zip_code?.trim() || record?.zipcode?.trim() || '',
      phone: record?.phone?.trim() || '',
      gender: record?.gender?.trim() || '',
      race: record?.race?.trim() || '',
      height: record?.height?.trim() || '',
      weight: record?.weight?.trim() || '',
      hair_color: record?.hair_color?.trim() || '',
      eye_color: record?.eye_color?.trim() || '',
      bond_amount: record?.bond_amount ? parseFloat(record?.bond_amount) : null,
      court_date: record?.court_date || null,
      status: record?.status || 'active'
    };
  }

  // New method to generate error summary
  generateErrorSummary(importLog) {
    const errorTypes = {};
    importLog?.forEach(error => {
      const type = error?.message?.split(':')?.[0] || 'Unknown';
      errorTypes[type] = (errorTypes?.[type] || 0) + 1;
    });

    const summary = Object.entries(errorTypes)?.map(([type, count]) => `${type}: ${count}`)?.join('; ');

    return `Errors encountered: ${summary}`;
  }

  // Get upload history
  async getUploadHistory(page = 1, limit = 20) {
    try {
      const offset = (page - 1) * limit;
      
      const { data, error, count } = await supabase?.from('upload_batches')?.select(`
          *,
          user_profile:user_profiles(
            full_name,
            email
          )
        `, { count: 'exact' })?.order('upload_date', { ascending: false })?.range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: data || [],
        total: count || 0,
        page,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message,
        data: [],
        total: 0,
        page: 1,
        totalPages: 0
      };
    }
  }

  // Get batch details
  async getBatchDetails(batchId) {
    try {
      const { data, error } = await supabase?.from('upload_batches')?.select(`
          *,
          user_profile:user_profiles(
            full_name,
            email
          )
        `)?.eq('id', batchId)?.single();

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }

  // Enhanced parseCSV with better error handling and header mapping
  parseCSV(csvContent, options = {}) {
    try {
      // Handle BOM if present
      if (csvContent?.charCodeAt(0) === 0xFEFF) {
        csvContent = csvContent?.slice(1);
      }

      const lines = csvContent?.split('\n')?.filter(line => line?.trim());
      if (lines?.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row');
      }

      const delimiter = options?.delimiter || this.detectDelimiter(lines?.[0]);
      const headers = this.parseCSVLine(lines?.[0], delimiter)?.map(h => h?.trim()?.toLowerCase());
      const records = [];
      const headerMapping = this.createHeaderMapping(headers);

      for (let i = 1; i < lines?.length; i++) {
        try {
          const values = this.parseCSVLine(lines?.[i], delimiter);
          const record = {};

          headers?.forEach((header, index) => {
            const value = values?.[index]?.trim() || '';
            const mappedField = headerMapping?.[header] || header;
            record[mappedField] = value;
          });

          records?.push(record);
        } catch (error) {
          // Log parsing error but continue with other rows
          console.warn(`CSV parsing error at line ${i + 1}: ${error?.message}`);
        }
      }

      return { success: true, records, headerMapping };
    } catch (error) {
      return { success: false, error: error?.message, records: [] };
    }
  }

  // New method to detect delimiter
  detectDelimiter(line) {
    const commaCount = (line?.match(/,/g) || [])?.length;
    const semicolonCount = (line?.match(/;/g) || [])?.length;
    
    return (semicolonCount > 2 && semicolonCount > commaCount) ? ';' : ',';
  }

  // Enhanced header mapping with comprehensive aliases
  createHeaderMapping(headers) {
    const mapping = {};
    const aliasMap = {
      // Name fields
      'arresteename': 'first_name',
      'arrestee name': 'first_name', 
      'name': 'first_name',
      'first': 'first_name',
      'fname': 'first_name',
      'lastname': 'last_name',
      'last': 'last_name',
      'lname': 'last_name',
      
      // Date fields
      'arresteedob': 'date_of_birth',
      'dob': 'date_of_birth',
      'date of birth': 'date_of_birth',
      'birthdate': 'date_of_birth',
      'arrdate': 'arrest_date',
      'arrest date': 'arrest_date',
      'arrestdate': 'arrest_date',
      'bookingdate': 'booking_date',
      'booking date': 'booking_date',
      
      // Case information
      'case num': 'case_number',
      'case': 'case_number',
      'casenumber': 'case_number',
      'booking num': 'booking_number',
      'booking': 'booking_number',
      'bookingnumber': 'booking_number',
      
      // Agency and facility
      'arragency': 'agency',
      'arresting agency': 'agency',
      'facility': 'facility',
      'jail': 'facility',
      'detention facility': 'facility',
      
      // Charges and narrative
      'synopsis': 'charges',
      'narrative': 'charges',
      'charge': 'charges',
      'offense': 'charges',
      'violation1': 'charges',
      'charge1': 'charges',
      
      // Financial
      'totalbail': 'bond_amount',
      'bail': 'bond_amount',
      'bail amount': 'bond_amount',
      
      // Warrants
      'outstandingwarrant1': 'warrant1',
      'warrant1': 'warrant1'
    };

    headers?.forEach(header => {
      const cleanHeader = header?.toLowerCase()?.trim();
      mapping[header] = aliasMap?.[cleanHeader] || header;
    });

    return mapping;
  }

  // New method for better CSV parsing
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line?.length; i++) {
      const char = line?.[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values?.push(current?.trim()?.replace(/['"]/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add the last value
    values?.push(current?.trim()?.replace(/['"]/g, ''));
    return values;
  }

  // New method for parsing Excel files
  async parseExcel(file) {
    try {
      // Dynamically import xlsx to avoid bundling issues
      const XLSX = await import('xlsx');
      
      const arrayBuffer = await file?.arrayBuffer();
      const workbook = XLSX?.read(arrayBuffer, { type: 'array' });
      
      // Get the first worksheet
      const worksheetName = workbook?.SheetNames?.[0];
      if (!worksheetName) {
        throw new Error('No worksheets found in Excel file');
      }
      
      const worksheet = workbook?.Sheets?.[worksheetName];
      
      // Convert to JSON
      const jsonData = XLSX?.utils?.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData?.length < 2) {
        throw new Error('Excel file must have at least a header row and one data row');
      }
      
      const headers = jsonData?.[0]?.map(h => String(h || '')?.trim()?.toLowerCase());
      const records = [];
      
      for (let i = 1; i < jsonData?.length; i++) {
        const row = jsonData?.[i];
        const record = {};
        
        headers?.forEach((header, index) => {
          const value = row?.[index] ? String(row?.[index])?.trim() : '';
          
          // Map common header variations
          const fieldMapping = {
            'first_name': ['first_name', 'firstname', 'first', 'fname'],
            'last_name': ['last_name', 'lastname', 'last', 'lname'],
            'date_of_birth': ['date_of_birth', 'dob', 'birth_date', 'birthdate'],
            'case_number': ['case_number', 'case_num', 'case', 'casenumber'],
            'booking_number': ['booking_number', 'booking_num', 'booking', 'bookingnumber'],
            'arrest_date': ['arrest_date', 'date_arrested', 'arrestdate'],
            'charges': ['charges', 'charge', 'offense', 'crime'],
            'bond_amount': ['bond_amount', 'bond', 'bail', 'bail_amount']
          };
          
          let fieldName = header;
          for (const [key, variations] of Object.entries(fieldMapping)) {
            if (variations?.includes(fieldName)) {
              fieldName = key;
              break;
            }
          }
          
          record[fieldName] = value;
        });
        
        records?.push(record);
      }
      
      return { success: true, records };
    } catch (error) {
      return { success: false, error: error?.message, records: [] };
    }
  }

  // Generate file hash
  async generateFileHash(file) {
    try {
      const buffer = await file?.arrayBuffer();
      const hashBuffer = await crypto.subtle?.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
    } catch (error) {
      return Date.now()?.toString(); // Fallback to timestamp
    }
  }

  // New method to get upload diagnostics
  async getUploadDiagnostics(limit = 15) {
    try {
      const { data, error } = await supabase?.rpc('get_upload_diagnostics', { limit_count: limit });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // New method to get upload errors for specific file
  async getUploadErrors(filename, limit = 10) {
    try {
      const { data, error } = await supabase?.rpc('get_upload_errors', { 
        batch_filename: filename, 
        error_limit: limit 
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // New method to analyze deduplication issues
  async analyzeDeduplication(daysBack = 2) {
    try {
      const { data, error } = await supabase?.rpc('analyze_dedup_collisions', { days_back: daysBack });

      if (error) {
        throw error;
      }

      return { success: true, data: data?.[0] || null };
    } catch (error) {
      return { success: false, error: error?.message, data: null };
    }
  }
}

export default new UploadService();